import { createTheme } from '@mui/material/styles';

// App-wide UI defaults. Primary purpose: stop <TextField select> fields from
// collapsing to an unreadable width (a recurring complaint), and make every
// page usable on a phone without switching to desktop mode. Applied globally
// via <ThemeProvider> in index.js, so it fixes every screen without touching
// the ~174 individual usages.
//
// getTheme(mode) builds the theme for 'light' or 'dark'. MUI flips all default
// surfaces (backgrounds, text, borders, Card/Dialog/Table) from palette.mode;
// the component overrides below are mode-agnostic and apply to both.
export const getTheme = (mode = 'light') => createTheme({
  palette: {
    mode,
    ...(mode === 'dark' ? {
      background: { default: '#0f1419', paper: '#161c24' },
    } : {}),
  },
  components: {
    MuiSelect: {
      styleOverrides: {
        // The value box of a Select — give it a sensible floor so the selected
        // text and the dropdown arrow are always visible. fullWidth selects are
        // unaffected (they stay 100% but never shrink below this).
        select: {
          minWidth: 100,
          paddingRight: 32, // room for the dropdown arrow so the value isn't clipped
        },
      },
    },
    // Dialogs: on phones, use nearly the whole screen instead of a squeezed box
    MuiDialog: {
      styleOverrides: {
        paper: {
          '@media (max-width:600px)': {
            margin: 8,
            width: 'calc(100% - 16px)',
            maxWidth: 'calc(100% - 16px)',
            maxHeight: 'calc(100% - 16px)',
          },
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          // MUI zeroes a DialogContent's padding-top when it directly follows a
          // DialogTitle. Because DialogContent also clips its overflow, the first
          // row's floating field captions (Stage Code, Term, Student Type, …) get
          // cut off as soon as they shrink to the top border. Restore a top pad
          // app-wide so no modal ever hides its field names. (This adjacent-
          // sibling selector outranks a plain sx={{ pt }}, so a dialog needing a
          // custom top pad should set it here or with matching specificity.)
          '.MuiDialogTitle-root + &': { paddingTop: 16 },
          '@media (max-width:600px)': { padding: '12px 14px' },
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          '@media (max-width:600px)': { padding: '12px 14px' },
        },
      },
    },
    // Tables: tighter cells on phones so more columns fit before scrolling
    MuiTableCell: {
      styleOverrides: {
        root: {
          '@media (max-width:600px)': { padding: '6px 8px', fontSize: 13 },
        },
      },
    },
    // Pagination toolbar wraps instead of overflowing on narrow screens
    MuiTablePagination: {
      styleOverrides: {
        toolbar: {
          '@media (max-width:600px)': { flexWrap: 'wrap', paddingLeft: 8 },
        },
      },
    },
  },
});

// Backward-compatible default (light) for any static import.
const theme = getTheme('light');
export default theme;
